import { useSyncExternalStore } from "react";

/**
 * Tracks whether files are currently being dragged anywhere over the window.
 * The listeners are registered once for all subscribers, so mounting this hook
 * on hundreds of ticket cards does not attach hundreds of window listeners.
 */

/** State */
let isDraggingFiles = false;
let dragDepth = 0;
const listeners = new Set<() => void>();

/** Functions */
function emit() {
	for (const listener of listeners) listener();
}

function setIsDraggingFiles(value: boolean) {
	if (isDraggingFiles === value) return;
	isDraggingFiles = value;
	emit();
}

function containsFiles(event: DragEvent) {
	return Array.from(event.dataTransfer?.types ?? []).includes("Files");
}

function handleDragEnter(event: DragEvent) {
	if (!containsFiles(event)) return;
	dragDepth += 1;
	setIsDraggingFiles(true);
}

function handleDragLeave(event: DragEvent) {
	if (!containsFiles(event)) return;
	dragDepth = Math.max(0, dragDepth - 1);
	if (dragDepth === 0) setIsDraggingFiles(false);
}

function handleDragEnd() {
	dragDepth = 0;
	setIsDraggingFiles(false);
}

function subscribe(listener: () => void) {
	if (listeners.size === 0) {
		window.addEventListener("dragenter", handleDragEnter);
		window.addEventListener("dragleave", handleDragLeave);
		window.addEventListener("drop", handleDragEnd);
		window.addEventListener("dragend", handleDragEnd);
	}
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
		if (listeners.size > 0) return;
		window.removeEventListener("dragenter", handleDragEnter);
		window.removeEventListener("dragleave", handleDragLeave);
		window.removeEventListener("drop", handleDragEnd);
		window.removeEventListener("dragend", handleDragEnd);
		dragDepth = 0;
		isDraggingFiles = false;
	};
}

function getSnapshot() {
	return isDraggingFiles;
}

/** Hook */
export const useIsDraggingFiles = () => {
	return useSyncExternalStore(subscribe, getSnapshot);
};
